import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, Fragment } from 'react';

import { Box, Grid, Divider, Typography } from '@mui/material';

import {
    startTour,
    TOUR_TYPES,
    completeTour,
    updateTourStep,
    selectIsSkipped,
    selectTourStatus,
    selectTutorialPreference,
} from 'store/slices/tour.slice';
import { posStyles } from './style/style';
import { snackAlertData } from 'utils/constants';
import SkyBoxModal from './components/SkyBoxModal';
import { CircleLoading, SnackBarAlert } from 'shared';
import { useCustomMediaQuery } from 'hooks/useMediaQuery';
import { skyboxTourSteps } from './utils/SkyboxTutorials';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import IntegrationCard from './components/IntegrationCard';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import JoyrideTour from 'components/Onboarding/Tour/JoyRideTour';
import AutomatiqIndyModal from './components/AutomatiqIndyModal';
import AutomatiqSyncModal from './components/AutomatiqSyncModal';
import { useCompanyService } from 'store/services/company.service';
import { useSkyboxService } from 'store/services/skybox.service';
import { integrationsData, automatedIntegrationsData } from './utils/integrationsData';

const tourId = TOUR_TYPES.SKYBOX_INTEGRATION;

const POSIntegration = () => {
    const dispatch = useDispatch();
    const { getCompanyIntegrations } = useCompanyService();
    const { syncSkyboxInvoices } = useSkyboxService();
    const companyData = useSelector((state) => state?.company?.data);
    const companyId = companyData?.id;
    const hasInvoiceAccess = companyData?.is_invoice_reconciler;
    const classes = posStyles({
        isScreenLowerThan1024: useCustomMediaQuery('(max-width: 1025px)'),
    });
    const isSkipped = useSelector((state) => selectIsSkipped(state, tourId));
    const tourStatus = useSelector((state) => selectTourStatus(state, tourId));
    const tutorialPreference = useSelector((state) => selectTutorialPreference(state));

    const [loading, setLoading] = useState(true);
    const [runTour, setRunTour] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [alertData, setAlertData] = useState(snackAlertData);
    const [companyIntegrations, setCompanyIntegrations] = useState({});

    const handleGetCompanyIntegrations = async () => {
        try {
            setLoading(true);
            const response = await getCompanyIntegrations(companyId);
            setCompanyIntegrations(response.data);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setLoading(false);
        }
    };

    const handleButtonClick = (name) => {
        setSelectedOption(name);
        setModalOpen(true);
    };

    const handleModalSuccess = (message) => {
        handleSuccessAlert(setAlertData, message);
        setModalOpen(false);
        handleGetCompanyIntegrations();
    };

    useEffect(() => {
        const manualRestart = tourStatus?.manuallyStarted;

        if (tutorialPreference === 'skipped' || isSkipped) {
            setRunTour(false);
        }
        const timer = setTimeout(() => {
            if (manualRestart) {
                setCurrentStep(0);
                dispatch(startTour({ tourId }));
                setRunTour(true);
            } else if (!tourStatus?.completed && !manualRestart) {
                if (tutorialPreference === 'skipped' || isSkipped) {
                    setRunTour(false);
                    return;
                }
                setRunTour(true);
                if (tourStatus?.started) {
                    setCurrentStep(tourStatus.currentStep);
                } else {
                    dispatch(startTour({ tourId }));
                    setCurrentStep(0);
                }
            } else {
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [dispatch, tourStatus?.completed, tourStatus?.manuallyStarted]);

    useEffect(() => {
        if (companyId) {
            handleGetCompanyIntegrations();
        }
    }, [companyId]);

    useEffect(() => {
        if (hasInvoiceAccess && companyIntegrations?.integrations_data?.skybox_connected) {
            syncSkyboxInvoices({ company_id: companyId });
        }
    }, [hasInvoiceAccess, companyIntegrations, companyId]);

    return (
        <Fragment>
            {loading ? (
                <CircleLoading />
            ) : (
                <Fragment>
                    <Grid
                        item
                        container
                        spacing={2}
                        sx={classes.personalInformation}
                        data-tour="pos-navigation"
                    >
                        <JoyrideTour
                            steps={skyboxTourSteps}
                            run={runTour}
                            stepIndex={currentStep}
                            continuous
                            scrollToFirstStep
                            showBeacon={true}
                            spotlightClicks={true}
                            callback={(event) => {
                                if (event.action === 'skip') {
                                    setRunTour(false);
                                    dispatch(completeTour({ tourId }));
                                }
                            }}
                            onStepBefore={(idx) => {
                                setCurrentStep(idx);
                                dispatch(updateTourStep({ tourId, stepIndex: idx }));
                            }}
                            onFinish={() => {
                                setModalOpen(true);
                                setSelectedOption('Skybox');
                            }}
                        />

                        <Grid item xs={12}>
                            <Typography fontWeight={700} fontSize={18}>
                                Point of Sale
                            </Typography>
                            <Typography
                                fontWeight={500}
                                fontSize={14}
                                color={(theme) => theme.primary.dark}
                            >
                                Connect your Point of Sale (POS) system with third-party platforms
                                like StubHub and SkyBox to streamline inventory, purchase tracking
                                and sales tracking.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Box width={'100%'} marginBottom={2}>
                                <Divider />
                            </Box>
                        </Grid>
                        {integrationsData(companyIntegrations).map((integration) => (
                            <Grid item xs={6} lg={4} key={integration.name}>
                                <IntegrationCard
                                    name={integration.name}
                                    logo={integration.logo}
                                    onClick={handleButtonClick}
                                    setCurrentStep={setCurrentStep}
                                    description={integration.description}
                                    isConnected={integration.isConnected}
                                    tourId={integration.name === 'SkyBox' ? 'skybox-card' : ''}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* {hasInvoiceAccess && (
                        <Grid
                            item
                            container
                            spacing={2}
                            marginTop={'20px'}
                            sx={classes.personalInformation}
                        >
                            <Grid item xs={12}>
                                <Typography fontWeight={700} fontSize={18}>
                                    Sync
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Box width={'100%'} marginBottom={2}>
                                    <Divider />
                                </Box>
                            </Grid>
                            {automatedIntegrationsData(companyIntegrations).map((integration) => (
                                <Grid item xs={6} lg={4} key={integration.name}>
                                    <IntegrationCard
                                        name={integration.name}
                                        logo={integration.logo}
                                        onClick={handleButtonClick}
                                        setCurrentStep={setCurrentStep}
                                        description={integration.description}
                                        isConnected={integration.isConnected}
                                        tourId={integration.name === 'SkyBox' ? 'skybox-card' : ''}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )} */}
                </Fragment>
            )}

            {modalOpen && selectedOption === 'SkyBox' && (
                <SkyBoxModal
                    tourId={tourId}
                    tourRun={runTour}
                    open={modalOpen}
                    currentStep={currentStep}
                    onSuccess={handleModalSuccess}
                    handleCloseModal={() => setModalOpen(false)}
                    isConnected={companyIntegrations?.integrations_data.skybox_connected}
                />
            )}

            {modalOpen && selectedOption === 'Automatiq Sync' && (
                <AutomatiqSyncModal
                    open={modalOpen}
                    onSuccess={handleModalSuccess}
                    onClose={() => setModalOpen(false)}
                    isConnected={companyIntegrations?.automatiq_sync_connected}
                />
            )}

            {modalOpen && selectedOption === 'Automatiq Indy' && (
                <AutomatiqIndyModal
                    open={modalOpen}
                    onSuccess={handleModalSuccess}
                    onClose={() => setModalOpen(false)}
                    isConnected={companyIntegrations?.automatiq_indy_connected}
                />
            )}

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
        </Fragment>
    );
};

export default POSIntegration;
